/**
 * @param condition - boolean that will be checked for response
 * @param truthyClass - class applied when the condition is true
 * @param falsyClass - class applied when the condition is false
 * @param commonClass - classes which are common in both condition
 *
 * Returns a string with the list of classes separated by spaces
 * in a string.
 */

import axios, { AxiosError } from 'axios'
import { useLayoutEffect, useState } from 'react'

import { Nav, NavItem } from '../../containers/_nav'
import {
    CLIENT_REWARDS,
    CLIENT_SIDEBAR_NAV,
    ESTHETICIAN_SIDEBAR_NAV,
    REWARDS,
    SIDEBAR_NAV,
    STORE,
    THE_DROP,
} from '../../containers/_sidenav'
import { MESSAGES, SYNDEO_API_ERROR_MESSAGES, UserRole } from '../constants'
import { CONTENT_BODY } from '../constants/enum'
import { Hit } from '../types/Algolia'
import {
    EventDetails,
    PointersBlock,
    SingleNavlink,
    Sliders,
} from '../types/ContentStack'
import { GetUserResponseType, ValidateOtpResponse } from '../types/auth'

import searchIcon from '../../assets/images/icons/charm_search.svg'
import { ReactComponent as Drop } from '../../assets/images/icons/drop.svg'
import { ReactComponent as Star } from '../../assets/images/icons/half-shaded-star.svg'
import { ReactComponent as History } from '../../assets/images/icons/history.svg'
import { ReactComponent as ProvideSmile } from '../../assets/images/icons/provider-smile-icon.svg'
import { ReactComponent as Report } from '../../assets/images/icons/report.svg'
import { ReactComponent as Setting } from '../../assets/images/icons/settings.svg'
import { ReactComponent as Smile } from '../../assets/images/icons/smile.svg'
import { ReactComponent as Store } from '../../assets/images/icons/store.svg'
import { ReactComponent as Trophy } from '../../assets/images/icons/trophy.svg'
import i18n from '../../i18n'
import { IUserLocation } from '../../router/pages/find-hydrafacialist'
import { timezones } from '../constants/timezones'
import { PLUS_ONE_PHONE_NUMBER, SYNDEO_PHONE_NUMBER } from '../regex'
import { IDeviceStyles, Styling } from '../types/common'

type ConditionalClasses = (
    condition: boolean,
    truthyClass: string,
    falsyClass?: string,
    commonClass?: string
) => string

export interface IUserLocationDetails {
    ISO3166_2_lvl4: string
    city: string
    country: string
    country_code: string
    postcode: string
    state: string
    state_district: string
    suburb: string
}

export const conditionalClasses: ConditionalClasses = (
    condition,
    truthyClass,
    falsyClass = '',
    commonClass = ''
) => {
    return `${commonClass} ${condition ? truthyClass : falsyClass}`
}

export const transformToSlider = (item: Sliders): PointersBlock => {
    return {
        __typename: CONTENT_BODY.POINTERS_BLOCK,
        pointers_block: {
            font_color: item.font_color,
            sub_title: item.sub_title,
            background_color: item.background_color,
            image_alignment: '',
            description: item.description,
            imageConnection: item.imageConnection,
            iconConnection: { edges: [] },
            imagegrid: item.imagegrid,
            title_imageConnection: item.title_imageConnection,
            banner_logoConnection: item.banner_logoConnection,
            link: item.link,
            link_multi: item.link_multi,
            click_id: item.click_id,
            pointers: item.pointers,
            title: item.title,
            title_description: item.title_description,
            type: '',
            footnote: item.footnote,
            mobile_imageConnection: item.mobile_imageConnection,
            class_name: item.class_name,
            custom_styling: item.custom_styling,
            is_footnote_below_cta_in_mobile_view:
                item.is_footnote_below_cta_in_mobile_view,
        },
    }
}

export const transformToEvent = (items: Hit): EventDetails => {
    return {
        title: items.title,
        start_date: items.start_date,
        end_date: items.end_date,
        location: items.location,
        start_date_timestamp: items.start_date_timestamp,
        image: {
            url: items.image.url,
            title: '',
        },
        link: {
            title: items.link.title,
            href: items.link.href,
        },
    }
}

export function useStickyHeader(offset: number, currentNav: boolean) {
    const [stick, setStick] = useState(false)
    const handleScroll = () => {
        if (currentNav) {
            setStick(window.scrollY >= 420)
        } else {
            setStick(window.scrollY >= offset)
        }
    }
    useLayoutEffect(() => {
        window.addEventListener('scroll', handleScroll)

        return () => {
            window.addEventListener('scroll', handleScroll)
        }
    })
    return stick
}

export const iconBasedOnLink = (type: string) => {
    switch (type) {
        case 'ACCOUNT SETTINGS':
            return <Setting />
        case 'REPORTS':
        case 'SYNDEO REPORTS':
            return <Report />
        case 'CLIENT REWARDS':
            return <Trophy />
        case 'TREATMENT HISTORY':
            return <History />
        case 'HYDRAFACIALISTS':
            return <Smile />
        case 'STORE':
            return <Store />
        case 'THE DROP':
            return <Drop />
        case 'PROVIDERS':
            return <ProvideSmile />
        case 'REWARDS PAGE':
            return <Star />
    }
}

/**
 * isError
 * @description this is a type helper that will make TS realize the type of the e object in catch block
 */

export const isError = (error: unknown): error is Error => {
    if (error instanceof Error) {
        return true
    }
    return false
}

export const transformToNavigationLink = (item: SingleNavlink): NavItem => {
    if (item.type === 'Search') {
        return {
            link: item.link.href,
            label: item.link.title,
            key: item.link.title,
            icon: searchIcon,
        }
    }
    if (item.type === 'Button') {
        return {
            link: item.link.href,
            label: item.link.title,
            key: item.link.title,
            type: 'button',
        }
    }
    return {
        link: item.link.href,
        label: item.link.title,
        key: item.link.title,
        click_id: item.click_id,
    }
}

/**
 * isAxiosError
 * @description this is a type helper that will make TS realize the type of error is AxiosError in catch block
 */
export const isAxiosError = (error: unknown): error is AxiosError =>
    axios.isAxiosError(error)

/**
 * isValidOtpResponse
 * @description determines the type of response recieved by from the `validateOtp` service call
 */
export const isValidOtpResponse = (
    response: object
): response is ValidateOtpResponse =>
    Object.hasOwn
        ? Object.hasOwn(response, 'accessToken')
        : response.hasOwnProperty('accessToken')

export const NavItems = (
    NAVS: Array<Nav>,
    userData: GetUserResponseType | undefined,
    rewardAppLaunch: string | boolean | undefined
) => {
    const role = userData?.userRole ?? ''
    const country = userData?.country ?? ''
    const memberId = userData?.memberId

    const navData = NAVS.map((item: Nav) => {
        if (item.key === REWARDS.key) {
            return {
                ...item,
                rewardAppLaunch: rewardAppLaunch
                    ? rewardAppLaunch === 'false'
                    : null,
            } as Nav
        }
        return item
    })

    if (
        (country === 'us' || country === 'ca') &&
        role === UserRole.Provider &&
        !memberId
    ) {
        return navData.filter(
            (item) => ![REWARDS.key, CLIENT_REWARDS.key].includes(item.key)
        )
    } else if (country !== 'us' && country !== 'ca') {
        return navData.filter(
            (item) =>
                ![
                    THE_DROP.key,
                    STORE.key,
                    REWARDS.key,
                    CLIENT_REWARDS.key,
                ].includes(item.key)
        )
    } else {
        return navData
    }
}

export const getDeviceType = () => {
    const userAgent = navigator.userAgent
    if (/android/i.test(userAgent)) {
        return 'Android'
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return 'iPhone/iOS'
    } else {
        return 'Unknown'
    }
}

export const getUserNav = (
    userData: GetUserResponseType | undefined,
    rewardAppLaunch: string | undefined
) => {
    switch (userData?.userRole) {
        case UserRole.Esthetician:
            return NavItems(ESTHETICIAN_SIDEBAR_NAV, userData, '')
        case UserRole.Provider:
            return NavItems(SIDEBAR_NAV, userData, '')
        case UserRole.Client:
            return NavItems(CLIENT_SIDEBAR_NAV, userData, rewardAppLaunch)
    }
}

export const GENERATE_POLICY_LINK = (language: string, route: string) => {
    switch (language) {
        case 'tc':
            return `${route}zh-TW`
        case 'sc':
            return `${route}zh-CN`
        case 'pg':
            return `${route}pt`
        case 'jp':
            return `${route}ja`
        case 'du':
            return `${route}de`
        case 'kr':
            return `${route}en`
        default:
            return `${route}${language}`
    }
}

/**
 * toSnakeCase
 * @description this method takes in a string `message` and then changes it into a snake case format of the same string removing all special characters.
 * @param {string} `message` the string that needs to be converted
 * @returns {string} the `message` in snake case
 */
export const toSnakeCase = (message: string): string =>
    message
        .toLowerCase()
        .replaceAll(/[^\w\s]/g, '')
        .replaceAll(/\s/g, '_')

/**
 * messageTranslationCode
 * @description this method checks the axios error `error` gets the message from the response's error object and checks it against  `SYNDEO_API_ERROR_MESSAGES` and returns the appropriate translation code
 * @param {AxiosError} `error` the error object from the calling function
 * @returns {string} the translation code
 */
export const getTranslationCode = (error: AxiosError): string => {
    if (error.response?.data?.error?.message) {
        const responseMsg = toSnakeCase(error.response.data.error.message)
        const messageCode = SYNDEO_API_ERROR_MESSAGES.includes(responseMsg)
            ? `api_messages.${responseMsg}`
            : MESSAGES.error.endpointError
        return messageCode
    } else {
        return MESSAGES.error.endpointError
    }
}

export const OTP_LANGUAGE = (language: string) => {
    switch (language) {
        case 'tc':
            return 'zh-TW'
        case 'sc':
            return 'zh-CN'
        case 'pg':
            return 'pt-PT'
        case 'jp':
            return 'ja-JP'
        case 'du':
            return 'de-DE'
        case 'fr':
            return 'fr-FR'
        case 'es':
            return 'es-ES'
        default:
            return 'en-US'
    }
}

export const isCountryAssociatedWithBeautyHealthId = (country: string) => {
    const countryList = ['us', 'ca']
    return countryList.includes(country)
}

export const extractParameters = (
    searchParams: URLSearchParams,
    params: string[]
): { [key: string]: string | null } => {
    let result: { [key: string]: string | null } = {}
    params.forEach((param) => {
        result[param] = searchParams.get(param) || null
    })
    return result
}

export const setCookie = (
    cname: string,
    cvalue: string,
    exdays: number = 0
): void => {
    const d = new Date()
    if (exdays !== 0) {
        d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000)
        let expires = 'expires=' + d.toUTCString()
        document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/'
    } else {
        document.cookie = cname + '=' + cvalue + ';path=/'
    }
}

export const getCookie = (cname: string): string => {
    let name = cname + '='
    let decodedCookie = decodeURIComponent(document.cookie)
    let ca = decodedCookie.split(';')
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) == ' ') {
            c = c.substring(1)
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length)
        }
    }
    return ''
}

type MyObject = {
    [key: string]: any
}

export const addUrlParams = (obj: MyObject, newValue: any): MyObject => {
    if (!obj.hasOwnProperty('urlParams') || obj.urlParams === null) {
        obj.urlParams = newValue
    }
    return obj
}

export const getHiddenInputsByName = (
    form: HTMLFormElement,
    names: string[]
): (HTMLInputElement | null)[] => {
    return names.map(
        (name) =>
            form.querySelector(
                `input[name="${name}"][type="hidden"]`
            ) as HTMLInputElement | null
    )
}

export const addValuesToInputs = (
    inputs: (HTMLInputElement | null)[],
    values: { [key: string]: string }
): void => {
    inputs.forEach((input) => {
        if (input && input.name && values.hasOwnProperty(input.name)) {
            input.value = values[input.name]
        }
    })
}

export const validatePhoneNumber = (value: string) => {
    const countryCode = value.split(' ')[0] // Extract country code
    const cleanedValue = value.replace(/[-.\s()]/g, '')
    const localPhoneNumber = cleanedValue.slice(countryCode.length)
    const isUSNumber = countryCode.startsWith('+1')
    const phoneNumberRegex = isUSNumber
        ? PLUS_ONE_PHONE_NUMBER
        : SYNDEO_PHONE_NUMBER

    // Country-specific minimum length based on country code length
    const minLength: Record<number, number> = {
        2: 9, // e.g., +XX country codes must have at least 9 digits
        3: 8, // e.g., +XXX country codes must have at least 8 digits
        4: 7, // e.g., +XXXX country codes must have at least 7 digits
    }

    const maxValidLength = 16 - countryCode.length // Maximum length for non-US numbers
    const minValidLength = minLength[countryCode.length] || 10 // Default minimum length if not specified

    if (localPhoneNumber.length === 0) {
        return {
            valid: false,
            error: 'authentication_flow_common_elements.enter_phone_error',
        }
    }

    if (/[a-zA-Z]/.test(value)) {
        // Check for letters in the input
        return {
            valid: false,
            error: 'authentication_flow_common_elements.phone_number_no_letters',
        }
    }

    // For US numbers, enforce exact length of 10 digits
    if (isUSNumber) {
        if (localPhoneNumber.length !== 10) {
            return {
                valid: false,
                error: 'authentication_flow_common_elements.phone_number_ten_digits',
            }
        }
    } else {
        // For non-US numbers, validate based on country code length
        if (localPhoneNumber.length < minValidLength) {
            return {
                valid: false,
                error: i18n.t(
                    'authentication_flow_common_elements.phone_number_too_short',
                    {
                        minLength: minValidLength,
                    }
                ),
            }
        }
        if (localPhoneNumber.length > maxValidLength) {
            return {
                valid: false,
                error: i18n.t(
                    'authentication_flow_common_elements.phone_number_too_long',
                    {
                        maxLength: maxValidLength,
                    }
                ),
            }
        }
    }

    // Consolidated regex check for both US and non-US numbers
    if (!phoneNumberRegex.test(cleanedValue)) {
        return {
            valid: false,
            error: i18n.t(
                'authentication_flow_common_elements.phone_valid_error'
            ),
        }
    }

    // If all checks pass, the phone number is valid
    return { valid: true }
}

export const formatPhoneNumber = (value: string) => {
    const cleaned = value?.replace(/[^\d+]/g, '') // Remove non-digit characters

    // Check if the country code starts with '+'
    if (cleaned?.startsWith('+')) {
        const countryCode = value.split(' ')[0] // Extract the country code
        const countryCodeLength = countryCode.length - 1 // Get the length of the country code (minus the '+')

        // Create regex dynamically based on the country code length
        const match = cleaned.match(
            new RegExp(
                `^(\\+\\d{1,${countryCodeLength}})(\\d{3})(\\d{3})(\\d{2,6})$`
            )
        )

        if (match) {
            // Format the output
            const formattedNumber = `${match[1]} (${match[2]}) ${match[3]}-${
                match[4] || ''
            }`.trim()
            return formattedNumber
        }
    }

    // Fallback for local numbers without a country code
    const localMatch = cleaned?.match(/^(\d{3})(\d{3})(\d{0,4})$/)
    if (localMatch) {
        return `(${localMatch[1]}) ${localMatch[2]}-${
            localMatch[3] || ''
        }`.trim()
    }

    return value.replace(/[^\d\s+]/g, '') // Return the value without letters if no matches found
}

export const getLocationThroughIP = async (): Promise<IUserLocation> => {
    try {
        const ipResponse = await axios.get('http://ipwho.is/')
        const locationDetails: IUserLocation = {
            lat: ipResponse.data.latittude,
            lng: ipResponse.data.longitude,
            country: ipResponse.data.country_code,
        }
        return locationDetails
    } catch (e) {
        throw e
    }
}

export const getLocationThroughTimeZone = async (): Promise<IUserLocation> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const country = (timezones as any)[timezone].c
        ? (timezones as any)[timezone].c[0]
        : (timezones as any)[(timezones as any)[timezone]]
    if (!country) {
        throw 'Country Not Found'
    }
    return {
        lat: 0,
        lng: 0,
        country: country,
    }
}

export const attachStyles = (
    deviceStyling: IDeviceStyles | undefined | null,
    isMobileView = false
) => {
    let styling: Styling | undefined = deviceStyling?.desktop as Styling
    if (isMobileView) {
        styling = {
            ...deviceStyling?.desktop,
            ...deviceStyling?.mobile,
        } as Styling
    }
    return (ref: HTMLElement | null) => {
        if (!ref || !styling) {
            return
        }
        for (let key in styling) {
            ref.style.setProperty(
                key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase(),
                styling[key as keyof Styling]?.toString() || '',
                'important'
            )
        }
    }
}

export const sortByDate = (list: any[], key: string) => {
    return list.sort((a, b) => {
        return new Date(b[key]).getTime() - new Date(a[key]).getTime()
    })
}

export function sortByCategoryOrder(category: string, dataArray: any[]) {
    return dataArray.sort((a, b) => {
        let indexA = getCategoryIndex(category, a.category_order)
        let indexB = getCategoryIndex(category, b.category_order)

        if (indexA === -1 && indexB === -1) {
            return 0
        }
        if (indexA === -1) {
            return 1
        }
        if (indexB === -1) {
            return -1
        }
        return indexA - indexB
    })
}

export function getCategoryIndex(
    category: string,
    categoryOrder: string | null
) {
    if (!categoryOrder) return -1

    try {
        const parsedOrder = JSON.parse(categoryOrder)
        for (const key in parsedOrder) {
            if (key.toLowerCase().includes(category.toLowerCase())) {
                return parsedOrder[key]
            }
        }
    } catch (e) {
        return -1
    }
    return -1
}

export const removeHubspotScripts = () => {
    try {
        // First, remove all script tags
        const selectors = [
            '[id^="hs"]',
            '[id*="hubspot"]',
            '[class*="hubspot"]',
            '[src*="hs-scripts.com"]',
            '[src*="hubspot"]',
            'script[src*="hs"]',
            'script[src*="hubspot"]',
            '#hs-script-loader',
        ]

        // Remove elements matching selectors
        selectors.forEach((selector) => {
            document.querySelectorAll(selector).forEach((element) => {
                if (element && element.parentNode) {
                    element.parentNode.removeChild(element)
                }
            })
        })

        // Clear Hubspot cookies
        document.cookie.split(';').forEach(function (c) {
            if (c.trim().toLowerCase().startsWith('hs')) {
                document.cookie = c
                    .replace(/^ +/, '')
                    .replace(
                        /=.*/,
                        '=;expires=' + new Date().toUTCString() + ';path=/'
                    )
            }
        })

        // Clear any Hubspot-related variables
        const hubspotVars = [
            '_hsq',
            'hbspt',
            'hsVars',
            'hsq',
            '_hsp',
            '_hssc',
            '_hstc',
            'hubspotutk',
        ]
        hubspotVars.forEach((varName) => {
            if (window.hasOwnProperty(varName)) {
                try {
                    delete (window as any)[varName]
                } catch (e) {
                    console.log(e)
                }
            }
        })

        // Remove any dynamically added Hubspot styles
        const styles = document.getElementsByTagName('style')
        Array.from(styles).forEach((style) => {
            if (style.innerHTML.includes('hubspot')) {
                style.parentNode?.removeChild(style)
            }
        })

        // Clear any localStorage items related to Hubspot
        Object.keys(localStorage).forEach((key) => {
            if (
                key.toLowerCase().includes('hubspot') ||
                key.toLowerCase().includes('hs-')
            ) {
                localStorage.removeItem(key)
            }
        })

        // Clear any sessionStorage items related to Hubspot
        Object.keys(sessionStorage).forEach((key) => {
            if (
                key.toLowerCase().includes('hubspot') ||
                key.toLowerCase().includes('hs-')
            ) {
                sessionStorage.removeItem(key)
            }
        })
    } catch (error) {
        console.error('Error cleaning up Hubspot:', error)
    }
}
